import { LoadingButton } from '@mui/lab'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Switch, Typography } from '@mui/material'
import * as controllers from 'controllers'
import { PersonReportsDto, ReportDto, UpdatePersonReportsDto } from 'dtos'
import { useEffect, useState } from 'react'

interface Props {
  initialValues: PersonReportsDto
  loading: boolean
  onClose: () => void
  onSave: (values: UpdatePersonReportsDto) => void
  open: boolean
}

export default function ReportsEditDialog({ initialValues, loading, onClose, onSave, open }: Props) {
  const [reports, setReports] = useState<ReportDto[]>([])
  const [selectedReportGuids, setSelectedReportGuids] = useState<string[]>([])
  const [isGettingReports, setIsGettingReports] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }

    setSelectedReportGuids(initialValues.reports.map(report => report.reportGuid))
    setIsGettingReports(true)
    controllers.getAvailableReports().then(response => setReports(response.value)).finally(() => setIsGettingReports(false))
  }, [initialValues, open])

  const toggleReport = (reportGuid: string) => {
    setSelectedReportGuids(current =>
      current.includes(reportGuid) ? current.filter(guid => guid !== reportGuid) : [...current, reportGuid]
    )
  }

  return (
    <Dialog id='edit-reports-dialog' onClose={loading ? undefined : onClose} open={open}>
      <DialogTitle>Edit Reports</DialogTitle>
      <DialogContent>
        <Grid container spacing={1} sx={{ pt: 1 }}>
          {isGettingReports && (
            <Grid item xs={12} container justifyContent='center'>
              <CircularProgress />
            </Grid>
          )}
          {!isGettingReports &&
            reports.map(report => (
              <Grid item xs={12} key={report.reportGuid}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedReportGuids.includes(report.reportGuid)}
                      disabled={loading}
                      id={`edit-report-${report.reportGuid}`}
                      onChange={() => toggleReport(report.reportGuid)}
                    />
                  }
                  label={
                    <span>
                      <Typography>{report.name}</Typography>
                      <Typography color='#808080' variant='body2'>
                        {report.description}
                      </Typography>
                    </span>
                  }
                />
              </Grid>
            ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color='inherit' disabled={loading} id='edit-reports-cancel' onClick={onClose}>
          CANCEL
        </Button>
        <LoadingButton
          id='edit-reports-save'
          loading={loading}
          onClick={() => onSave({ reportGuids: selectedReportGuids })}
          variant='contained'
        >
          SAVE
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
